import React from 'react';
import { ChevronLeftIcon, LightbulbIcon, ConstructionIcon, Trash2Icon, TreesIcon, FileTextIcon, CheckCircle2Icon, CircleIcon, MapPinIcon, PlusIcon, SignalIcon, WifiIcon, BatteryFullIcon, SettingsIcon, CircleCheckIcon } from 'lucide-react';
import { citizenMock, atnaMock, fieldMock, trackingMock, FIELD_PHOTOS } from '../../../data/richelieuProof';
type P = {
  isFR: boolean;
};
const pickFn = (isFR: boolean) => (v: {
  fr: string;
  en: string;
}) => isFR ? v.fr : v.en;
function Phone({
  children,
  label
}: {
  children: React.ReactNode;
  label: string;
}) {
  return <div className="relative mx-auto w-full max-w-[200px] rounded-[30px] border-[6px] border-[#16181F] bg-white shadow-[0_18px_36px_-22px_rgba(20,20,40,0.55)] overflow-hidden" role="img" aria-label={label} data-id="element-5966" data-mp="smdv7trpmlfo">
      <div className="flex items-center justify-between px-4 pt-2 pb-1 font-sans text-[8.5px] font-semibold text-[#121212]" data-id="element-5967" data-mp="iozdzrxypsmb">
        <span data-id="element-5968" data-mp="novjxwq7zu4i">9:41</span>
        <span className="w-12 h-3.5 rounded-full bg-[#16181F]" aria-hidden="true" data-id="element-5969" data-mp="2iyeprqhx8mt2" />
        <span className="flex items-center gap-0.5" aria-hidden="true" data-id="element-5970" data-mp="2j07yrcg0bj48">
          <SignalIcon className="w-2.5 h-2.5" data-id="element-5971" data-mp="2dysbddqcx0io" />
          <WifiIcon className="w-2.5 h-2.5" data-id="element-5972" data-mp="2rytd0veg4bz7" />
          <BatteryFullIcon className="w-3 h-3" data-id="element-5973" data-mp="2ryjnf8xbc6u1" />
        </span>
      </div>
      <div className="px-3 pb-4" data-id="element-5974" data-mp="2nx86drsi8txg">{children}</div>
    </div>;
}
const CAT_ICONS = {
  light: LightbulbIcon,
  roads: ConstructionIcon,
  waste: Trash2Icon,
  parks: TreesIcon,
  permits: FileTextIcon
} as const;
export function CitizenMock({
  isFR
}: P) {
  const pick = pickFn(isFR);
  return <Phone label={pick(citizenMock.title)} data-id="element-5975" data-mp="290qvt2s53jby">
      <ChevronLeftIcon className="w-3.5 h-3.5 text-[#4A5160] mt-1" aria-hidden="true" data-id="element-5976" data-mp="291s9zg4gclxw" />
      <p className="font-sans text-[11px] font-semibold text-[#121212] mt-2" data-id="element-5977" data-mp="248pe31c7d0ej">{pick(citizenMock.title)}</p>
      <ul className="mt-3 space-y-1.5" data-id="element-5978" data-mp="1gid7puwlvbq2">
        {citizenMock.categories.map((c, i) => {
        const Icon = CAT_ICONS[c.key as keyof typeof CAT_ICONS];
        const active = i === 0;
        return <li key={c.key} className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 font-sans text-[9.5px] ${active ? 'bg-[#EEEDFD] text-[#4F46E5] font-medium' : 'bg-[#F9F9FB] text-[#2A2F3C]'}`} data-id="element-5979" data-mp="3bmt25ywcl7w2" data-mpi={i}>
              <Icon className={`w-3 h-3 ${active ? 'text-[#E3A21A]' : 'text-[#4A5160]'}`} strokeWidth={1.8} aria-hidden="true" data-id="element-5980" data-mp="3qanuvfacvc95" />
              {pick(c.label)}
            </li>;
      })}
      </ul>
      <span className="mt-4 block rounded-md bg-[#4F46E5] py-2 text-center font-sans text-[10px] font-semibold text-white" data-id="element-5981" data-mp="2xo36mnp6fg9t">
        {pick(citizenMock.submit)}
      </span>
    </Phone>;
}
export function AtnaMock({
  isFR
}: P) {
  const pick = pickFn(isFR);
  const sep = isFR ? ' : ' : ': ';
  return <div className="mx-auto w-full max-w-[236px] rounded-xl border border-[#E8E8F0] bg-white p-4 shadow-[0_14px_30px_-24px_rgba(40,36,120,0.45)]" data-id="element-5982" data-mp="hvnn26yjn0cv">
      <div className="flex items-center justify-between" data-id="element-5983" data-mp="27d1d7pjkx6p6">
        <p className="font-sans text-[11.5px] font-semibold text-[#121212]" data-id="element-5984" data-mp="27chsk12jo47g">{pick(atnaMock.title)}</p>
        <span className="rounded-md bg-[#4F46E5] px-1.5 py-0.5 font-sans text-[8.5px] font-bold text-white" data-id="element-5985" data-mp="22ezs6z2lexb2">{isFR ? 'IA' : 'AI'}</span>
      </div>
      <ul className="mt-3 space-y-2.5" data-id="element-5986" data-mp="1sowqmaheob14">
        {atnaMock.checks.map(c => <li key={c.k.en} className="flex items-center gap-2.5 font-sans text-[10.5px] text-[#4A5160]" data-id="element-5987" data-mp="1npbtm5lsqcr1">
            <CheckCircle2Icon className="w-3.5 h-3.5 text-white fill-[#22A559] shrink-0" aria-hidden="true" data-id="element-5988" data-mp="1jnyknmneptfw" />
            <span data-id="element-5989" data-mp="1eo3tdhzpz1ub">
              {pick(c.k)}
              {sep}
              <span className="text-[#121212]" data-id="element-5990" data-mp="1eqq3npdzvvg8">{pick(c.v)}</span>
            </span>
          </li>)}
      </ul>
      <p className="flex items-center gap-1.5 font-sans text-[9.5px] text-[#6B7180] mt-2.5 pl-6" data-id="element-5991" data-mp="392mubg4m4xa8">
        <MapPinIcon className="w-3 h-3" aria-hidden="true" data-id="element-5992" data-mp="343k0ysxzwfi3" />
        {atnaMock.address}
      </p>
      <div className="mt-3 rounded-lg bg-[#F4F3FD] px-3 py-2.5" data-id="element-5993" data-mp="21q5p47xvcd6p">
        <p className="font-sans text-[11px] font-semibold text-[#4F46E5]" data-id="element-5994" data-mp="21qx9snpxgqi7">{pick(atnaMock.contextTitle)}</p>
        <ul className="mt-1.5 space-y-1.5" data-id="element-5995" data-mp="25pmwx80rpmf1">
          {atnaMock.context.map(c => <li key={c.k.en} className="flex items-center gap-2 font-sans text-[9.5px] text-[#4F46E5]" data-id="element-5996" data-mp="1rrg9ymxcx6u5">
              <PlusIcon className="w-2.5 h-2.5 shrink-0" aria-hidden="true" data-id="element-5997" data-mp="1wp7z596maeun" />
              <span data-id="element-5998" data-mp="1i1d59pgvi4fz">
                {pick(c.k)}
                {sep}
                {pick(c.v)} {c.small && <span className="text-[8px] text-[#7A74D8]" data-id="element-5999" data-mp="1mtog3nd5janb">{pick(c.small)}</span>}
              </span>
            </li>)}
        </ul>
      </div>
    </div>;
}
export function FieldMock({
  isFR
}: P) {
  const pick = pickFn(isFR);
  return <Phone label={pick(fieldMock.title)} data-id="element-6000" data-mp="z3ak5lrg4rng">
      <div className="flex items-center justify-between mt-1" data-id="element-6001" data-mp="z4bxjwvxpq8c">
        <p className="font-sans text-[10.5px] font-semibold text-[#121212]" data-id="element-6002" data-mp="14240dly4awrb">{pick(fieldMock.title)}</p>
        <SettingsIcon className="w-3 h-3 text-[#9AA0AE]" aria-hidden="true" data-id="element-6003" data-mp="kgbunwc5g7bd" />
      </div>
      <div className="mt-2 flex items-center gap-2.5 border-b border-[#EEEEF4] font-sans text-[8.5px]" data-id="element-6004" data-mp="gf04nppd5k2n">
        {fieldMock.tabs.map(t => <span key={t.label.en} className={`pb-1.5 flex items-center gap-1 whitespace-nowrap ${t.active ? 'text-[#4F46E5] border-b-2 border-[#4F46E5] -mb-px' : 'text-[#6B7180]'}`} data-id="element-6005" data-mp="ufkmaijjl9uk">
            {pick(t.label)}
            {t.count && <span className="rounded bg-[#EEEDFD] px-1 text-[7.5px] text-[#4F46E5]" data-id="element-6006" data-mp="zdulr5cauwlz">{t.count}</span>}
          </span>)}
      </div>
      <div className="mt-2.5 flex items-start justify-between gap-1" data-id="element-6007" data-mp="1d47sx5hda7a9">
        <div data-id="element-6008" data-mp="185o2arrtw7bk">
          <p className="flex items-center gap-1.5 font-sans text-[9.5px] text-[#121212]" data-id="element-6009" data-mp="185wgqo3lnkov">
            <LightbulbIcon className="w-3 h-3 text-[#4F46E5]" aria-hidden="true" data-id="element-6010" data-mp="1n2x6im28d5kp" />
            {pick(fieldMock.task.category)}
          </p>
          <p className="font-sans text-[8.5px] text-[#6B7180] pl-[18px] mt-0.5" data-id="element-6011" data-mp="1i5x0gb5ds2tl">{fieldMock.task.id}</p>
          <p className="font-sans text-[8.5px] text-[#6B7180] pl-[18px]" data-id="element-6012" data-mp="1p9ddx1wfzgei">{fieldMock.task.street}</p>
        </div>
        <span className="rounded bg-[#EEEDFD] px-1.5 py-0.5 font-sans text-[7.5px] text-[#4F46E5] whitespace-nowrap" data-id="element-6013" data-mp="1u4kdbxiohyxm">{pick(fieldMock.task.status)}</span>
      </div>
      <div className="mt-2 grid grid-cols-[1.6fr_1fr] gap-1.5" data-id="element-6014" data-mp="23x87slm6rqt6">
        <img src={FIELD_PHOTOS.pole} alt="" className="h-14 w-full rounded object-cover" data-id="element-6015" data-mp="28vt5ada1zwdx" />
        <img src={FIELD_PHOTOS.lamp} alt="" className="h-14 w-full rounded object-cover" data-id="element-6016" data-mp="2cwuxia5jz86e" />
      </div>
      <ul className="mt-2.5 space-y-1.5" data-id="element-6017" data-mp="2hwhb4kpamqfq">
        {fieldMock.steps.map(s => <li key={s.label.en} className={`flex items-center gap-2 font-sans text-[9px] ${s.done ? 'text-[#2A2F3C]' : 'text-[#9AA0AE]'}`} data-id="element-6018" data-mp="2huep0br9hhga">
            {s.done ? <CheckCircle2Icon className="w-3 h-3 text-white fill-[#22A559] shrink-0" aria-hidden="true" data-id="element-6019" data-mp="njri056r1l9e" /> : <CircleIcon className="w-3 h-3 text-[#D5D7DF] fill-[#EEEEF4] shrink-0" aria-hidden="true" data-id="element-6020" data-mp="sj4ezirc48pp" />}
            {pick(s.label)}
            {!s.done && <span className="h-1 w-8 rounded-full bg-[#EEEEF4]" aria-hidden="true" data-id="element-6021" data-mp="g6a2xk6airv4" />}
          </li>)}
      </ul>
    </Phone>;
}
export function TrackingMock({
  isFR
}: P) {
  const pick = pickFn(isFR);
  return <div className="relative mx-auto w-full max-w-[210px]" data-id="element-6022" data-mp="6ap8c73jwtve">
      <Phone label={pick(trackingMock.title)} data-id="element-6023" data-mp="13wwdpj7oiq8t">
        <p className="font-sans text-[10.5px] font-semibold text-[#121212] mt-2" data-id="element-6024" data-mp="13ujuz3u3kbup">{pick(trackingMock.title)}</p>
        <ol className="mt-3 relative pb-16" data-id="element-6025" data-mp="yvp5xk5jdr25">
          <span className="absolute left-[6px] top-2 bottom-20 w-px bg-[#CFEBD8]" aria-hidden="true" data-id="element-6026" data-mp="txf811sfgccu" />
          {trackingMock.steps.map(s => <li key={s.label.en} className="relative flex gap-2.5 pb-2.5" data-id="element-6027" data-mp="p1q3sl5gq7a7">
              <CheckCircle2Icon className="relative w-3.5 h-3.5 text-white fill-[#22A559] shrink-0 bg-white rounded-full" aria-hidden="true" data-id="element-6028" data-mp="3eq8tsjxzd7zw" />
              <span data-id="element-6029" data-mp="38m272ry37pu">
                <span className="block font-sans text-[9.5px] text-[#121212]" data-id="element-6030" data-mp="382suagd1rnf">{pick(s.label)}</span>
                <span className="block font-sans text-[8px] text-[#8A90A0]" data-id="element-6031" data-mp="87p6gl03p9wr">{pick(s.time)}</span>
              </span>
            </li>)}
        </ol>
      </Phone>
      <div className="absolute left-[-6%] right-[-6%] bottom-3 flex items-start gap-2.5 rounded-xl border border-[#D6EEDC] bg-[#F0F9F2] px-3 py-2.5 shadow-[0_10px_24px_-18px_rgba(20,80,40,0.5)]" data-id="element-6032" data-mp="3uene1djqi5cj">
        <CircleCheckIcon className="w-5 h-5 text-[#22A559] shrink-0" strokeWidth={1.6} aria-hidden="true" data-id="element-6033" data-mp="qxx4iooy1a63" />
        <p className="font-sans text-[9.5px] text-[#1F3A2A] leading-snug" data-id="element-6034" data-mp="d6s0laejlvpy">{pick(trackingMock.success)}</p>
      </div>
    </div>;
}